/**
 * Normalizes raw data source records from the engine data API
 * (GET /api/data/sources) into the shape the Advanced Query builder renders.
 *
 * The engine returns snake_case Postgres columns: `sustainability_pillar`,
 * `update_frequency`, lowercase pillar strings, and TEXT[] arrays for
 * `keywords` and `category`. This module maps those to the camelCase,
 * Title-cased shape the source selector expects, so the UI never has to know
 * about the database column names.
 */

// Lowercase DB pillar value -> Title-cased label used by the selector's
// pillar grouping and color config.
const PILLAR_LABELS = {
    environmental: 'Environmental',
    social: 'Social',
    economic: 'Economic',
};

/**
 * Title-case a raw sustainability pillar string, falling back to 'Other'
 * for null/unknown values so grouping never produces an undefined bucket.
 * @param {string} pillar - Raw pillar value from the API (e.g. 'environmental')
 * @returns {string} One of 'Environmental' | 'Social' | 'Economic' | 'Other'
 */
export function normalizePillar(pillar) {
    if (!pillar) return 'Other';
    return PILLAR_LABELS[pillar.toLowerCase()] || 'Other';
}

/**
 * Pick a single display label from a TEXT[] category array. The selector shows
 * one small badge, so we take the first entry; a plain string is passed through
 * for resilience, and anything else becomes an empty string.
 * @param {string[]|string} category
 * @returns {string}
 */
function firstCategory(category) {
    if (Array.isArray(category)) return category[0] || '';
    if (typeof category === 'string') return category;
    return '';
}

/**
 * Map one raw API source record to the builder's source shape.
 * @param {Object} raw - A record from `data_sources` in the API response
 * @returns {Object} Normalized source
 */
export function normalizeDataSource(raw) {
    return {
        id: raw.id,
        name: raw.name,
        description: raw.description || '',
        pillar: normalizePillar(raw.sustainability_pillar),
        category: firstCategory(raw.category),
        organization: raw.organization || '',
        updateFrequency: raw.update_frequency || 'Not specified',
        keywords: Array.isArray(raw.keywords) ? raw.keywords : [],
        apiType: raw.api_type || null,
        earliestDate: raw.earliest_date || null,
        newestDate: raw.newest_date || null,
    };
}

/**
 * Deduplicated, alphabetically sorted union of keywords across the selected
 * sources. This replaces the mock `getKeywordsForSources` helper, operating on
 * already-normalized sources instead of the hardcoded array. Sources whose id
 * is not in `selectedIds` are ignored.
 * @param {Object[]} sources - Normalized sources (each with a `keywords` array)
 * @param {Array<number|string>} selectedIds - Ids of the currently selected sources
 * @returns {string[]} Sorted, unique keyword fields
 */
export function deriveKeywordFields(sources, selectedIds) {
    const selected = new Set(selectedIds);
    const keywords = new Set();
    sources.forEach((source) => {
        if (selected.has(source.id)) {
            source.keywords.forEach((keyword) => keywords.add(keyword));
        }
    });
    return Array.from(keywords).sort();
}
